import { CustomLayout, CustomTrace } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { getCustomTraceByKey } from 'src/Libs/system-charts/shared/utils/getCustomTraceByKey'
import { TraceKey } from '../../shared/config/traceConf'
import { isConsumerLine, isProducerLine } from '../../shared/utils/chart-lines'
import { PowermeterHardwareDataModel } from 'src/State/Storage/StorageModel'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import { getFilteredAvailableTraceKeysForDefaultChart } from 'src/Libs/chart-kit/shared/utils/available-lines-fns'
import { UnitTypes } from 'src/Libs/types'
import type { CostSavingChartTraceKey } from 'src/Libs/dynamic-power-prices/cost-saving-chart-trace-keys'

export const DEFAULT_CHART_ID = 0
export const DEFAULT_POC_CHART_ID = 8

export const getInitialProductionPieChart = (maxHeight: number): CustomLayout => ({
  id: 2,
  isInitial: true,
  title: 'pieProduction',
  labelNameKey: 'chart.pie-chart.production.label',
  labelTraces: ['SelfProduction'],
  unit: UnitTypes.wattHour,
  minPrefix: 1,
  views: {
    hour: 'pie',
    day: 'pie',
    week: 'pie',
    month: 'pie',
    year: 'pie',
  },
  traces: [getCustomTraceByKey('SelfProduction'), getCustomTraceByKey('NetIn')],
  maxHeight,
})

export const getInitialHouseConsumptionPieChart = (maxHeight: number, isDynamicPowerPriceEnabled: boolean): CustomLayout => {
  const pieTraceAutarky = getCustomTraceByKey('Autarky')
  const pieTraceThreeSixtySavings = getCustomTraceByKey('ThreeSixtySavings')
  const pieTraceNetOut = getCustomTraceByKey(isDynamicPowerPriceEnabled ? 'NetOutWithoutThreeSixtySavings' : 'NetOut')
  const labelNameKey = isDynamicPowerPriceEnabled ? 'chart.pie-chart.home-consumption.label' : 'live-values.pie-chart.home-consumption.label'

  return {
    id: 3,
    isInitial: true,
    title: 'pieConsumptionHouse',
    labelNameKey,
    labelTraces: ['Autarky', 'ThreeSixtySavings'],
    unit: UnitTypes.wattHour,
    minPrefix: 1,
    views: {
      hour: 'pie',
      day: 'pie',
      week: 'pie',
      month: 'pie',
      year: 'pie',
    },
    traces: [pieTraceAutarky, pieTraceThreeSixtySavings, pieTraceNetOut],
    maxHeight,
  }
}

const getPreset0 = (maxHeight: number): CustomLayout => ({
  id: DEFAULT_CHART_ID,
  isInitial: true,
  title: 'default',
  summaryUnit: UnitTypes.wattHour,
  hasSummary: false,
  unit: UnitTypes.watt,
  summaryMinPrefix: 1,
  views: {
    hour: 'line',
    day: 'line',
    week: 'line',
    month: 'bar',
    year: 'bar',
  },
  traces: [],
  maxHeight,
})

/**
 * @beta This logic is part of the system chart proof of concept and should not be made available to users yet
 */
const getPocConfig = (maxHeight: number): CustomLayout => ({
  id: DEFAULT_POC_CHART_ID,
  isInitial: true,
  title: 'poc',
  summaryUnit: UnitTypes.wattHour,
  hasSummary: false,
  unit: UnitTypes.watt,
  summaryMinPrefix: 1,
  views: {
    hour: 'line',
    day: 'line',
    week: 'line',
    month: 'bar',
    year: 'bar',
  },
  traces: [],
  maxHeight,
})

const getDirectConsumptionPreset = (maxHeight: number, defaultTraces: CustomTrace[]): CustomLayout => ({
  id: 4,
  isInitial: true,
  title: 'direktConsumption',
  summaryUnit: UnitTypes.wattHour,
  hasSummary: true,
  unit: UnitTypes.watt,
  summaryMinPrefix: 1,
  views: {
    hour: 'bar',
    day: 'line',
    week: 'line',
    month: 'bar',
    year: 'bar',
  },
  traces: defaultTraces,
  maxHeight,
})

const getDirectConsumptionProductionDefaultPreset = (maxHeight: number, defaultTraces: CustomTrace[]): CustomLayout => ({
  id: 7,
  isInitial: true,
  title: 'direktConsumptionProduction',
  summaryUnit: UnitTypes.wattHour,
  unit: UnitTypes.watt,
  hasSummary: true,
  summaryMinPrefix: 1,
  views: {
    hour: 'bar',
    day: 'line',
    week: 'line',
    month: 'bar',
    year: 'bar',
  },
  traces: defaultTraces,
  maxHeight,
})

const getBuildingConsumptionDefaultPreset = (maxHeight: number, defaultTraces: CustomTrace[]): CustomLayout => ({
  id: 5,
  isInitial: true,
  title: 'buildingConsumption',
  secondUnit: UnitTypes.celsius,
  summaryUnit: UnitTypes.wattHour,
  summaryMinPrefix: 1,
  views: {
    hour: 'line',
    day: 'line',
    week: 'line',
    month: 'line',
    year: 'line',
  },
  traces: defaultTraces,
  maxHeight,
})

const getYearComparisonDefaultPreset = (maxHeight: number, defaultTraces: CustomTrace[]): CustomLayout => ({
  id: 6,
  isInitial: true,
  title: 'priorYearComparison',
  unit: UnitTypes.wattHour,
  minPrefix: 1,
  views: {
    hour: 'comparison',
    day: 'comparison',
    week: 'comparison',
    month: 'comparison',
    year: 'comparison',
  },
  traces: defaultTraces,
  maxHeight,
})

export const getYearComparisonPreset = (): CustomLayout => {
  const houseConsumptionTraceComparison = getCustomTraceByKey('ConsumptionHouse')

  houseConsumptionTraceComparison.suffixColors = [
    {
      traceName: 'ConsumptionHouse',
      color: 'blue',
    },
    {
      traceName: 'ConsumptionHouse',
      color: 'yellow',
    },
  ]

  const solarTrace = getCustomTraceByKey('DC_PowerSolar')
  solarTrace.suffixColors = [
    { traceName: 'DC_PowerSolar', color: 'red' },
    { traceName: 'DC_PowerSolar', color: 'green' },
  ]

  const yearComparison = getYearComparisonDefaultPreset(500, [houseConsumptionTraceComparison, solarTrace])
  return yearComparison
}

export const getInitialPerformanceValueChart = (
  powerValuesChartHeight: number,
  availableLines: TraceKey[],
  wallboxes?: WallboxHardwareModel[],
  powermeters?: PowermeterHardwareDataModel
): CustomLayout => {
  const defaultPreset = getPreset0(powerValuesChartHeight)
  const filteredAvailableTraceKeys = getFilteredAvailableTraceKeysForDefaultChart(availableLines, wallboxes, powermeters)

  for (const traceKey of filteredAvailableTraceKeys) {
    const customTrace = getCustomTraceByKey(traceKey)
    /* turn off summable traces */
    let offByDefault: true | undefined = undefined
    if (isConsumerLine(traceKey) && availableLines.includes('ConsumptionTotal')) {
      offByDefault = true
    }
    if (isProducerLine(traceKey) && availableLines.includes('DC_PowerTotal')) {
      offByDefault = true
    }
    if (customTrace) defaultPreset.traces.push({ ...customTrace, offByDefault })
  }

  return defaultPreset
}
/**
 * Get initial chart layout for the PocPerformantChart
 *
 * @beta This logic is part of the system chart proof of concept and should not be made available to users yet
 */
export const getInitialPocPerformantChart = (
  powerValuesChartHeight: number,
  availableLines: TraceKey[],
  wallboxes?: WallboxHardwareModel[],
  powermeters?: PowermeterHardwareDataModel
): CustomLayout => {
  const defaultPreset = getPocConfig(powerValuesChartHeight)
  const filteredPocTraces = getFilteredAvailableTraceKeysForDefaultChart(availableLines, wallboxes, powermeters)

  for (const traceKey of filteredPocTraces) {
    const customTrace = getCustomTraceByKey(traceKey)
    /* turn off summable traces */
    let offByDefault: true | undefined = undefined
    if (isConsumerLine(traceKey) && availableLines.includes('ConsumptionTotal')) {
      offByDefault = true
    }
    if (isProducerLine(traceKey) && availableLines.includes('DC_PowerTotal')) {
      offByDefault = true
    }
    if (customTrace) defaultPreset.traces.push({ ...customTrace, offByDefault })
  }

  return defaultPreset
}

export const getDefaultDirectConsumptionPreset = (
  consumptionChartsHeight: number,
  powermeters: PowermeterHardwareDataModel,
  wallboxes: WallboxHardwareModel[]
): CustomLayout => {
  let directConsumptionProductionSumLineKey = 'ConsumptionHouse'
  if (powermeters?.consumers.length || wallboxes?.length) {
    directConsumptionProductionSumLineKey = 'ConsumptionTotal'
  }

  const directConsumptionTrace = getCustomTraceByKey('DirectConsumption')
  directConsumptionTrace.stackId = 1

  const batPowerOutTrace = getCustomTraceByKey('BatPowerOut')
  const netOutTrace = getCustomTraceByKey('NetOut')
  const sumTrace = getCustomTraceByKey(directConsumptionProductionSumLineKey)
  batPowerOutTrace.stackId = 1
  netOutTrace.stackId = 1
  sumTrace.hidden = true

  const directConsumptionPreset = getDirectConsumptionPreset(consumptionChartsHeight, [
    directConsumptionTrace,
    batPowerOutTrace,
    netOutTrace,
    sumTrace,
  ])

  return directConsumptionPreset
}

export const getBuildingConsumptionPreset = (buildingConsumptionHeight: number): CustomLayout => {
  const houseConsumptionTrace = getCustomTraceByKey('ConsumptionHouse')
  houseConsumptionTrace.type = 'bar'
  houseConsumptionTrace.stackId = 1
  const wallboxSumTrace = getCustomTraceByKey('Wallbox_energyAll')
  const wallboxDischargedSumTrace = getCustomTraceByKey('Wallbox_energyDischarged')

  wallboxSumTrace.type = 'bar'
  wallboxSumTrace.stackId = 1
  wallboxDischargedSumTrace.type = 'bar'
  wallboxDischargedSumTrace.stackId = 1

  const temperatureTrace = getCustomTraceByKey('Temperature')
  temperatureTrace.type = 'line'

  const buildingConsumption = getBuildingConsumptionDefaultPreset(buildingConsumptionHeight, [
    houseConsumptionTrace,
    wallboxSumTrace,
    wallboxDischargedSumTrace,
    temperatureTrace,
  ])

  return buildingConsumption
}

export const getDirectConsumptionProductionPreset = (consumptionChartsHeight: number): CustomLayout => {
  const batPowerInTrace = getCustomTraceByKey('BatPowerIn')
  const netInTrace = getCustomTraceByKey('NetIn')
  const powerTotalTrace = getCustomTraceByKey('DC_PowerTotal')
  const forecastPowerTrace = getCustomTraceByKey('ForecastPower')
  const directConsumptionTrace = getCustomTraceByKey('DirectConsumption')

  batPowerInTrace.stackId = 1
  netInTrace.stackId = 1
  powerTotalTrace.stackId = 1
  powerTotalTrace.hidden = true
  directConsumptionTrace.stackId = 1

  const directConsumptionProductionPreset = getDirectConsumptionProductionDefaultPreset(consumptionChartsHeight, [
    powerTotalTrace,
    directConsumptionTrace,
    batPowerInTrace,
    netInTrace,
    forecastPowerTrace,
  ])

  return directConsumptionProductionPreset
}

export const getCostSavingsPieChartPreset = (
  maxHeight: number,
  defaultTraces: CustomTrace[],
  labelTraces: CostSavingChartTraceKey[]
): CustomLayout => ({
  id: 1,
  isInitial: true,
  title: 'pieSavings',
  labelNameKey: 'dynamic-power-prices.cost-saving-chart.savings-pie-chart.label-name',
  labelTraces: labelTraces,
  unit: UnitTypes.euros,
  secondUnit: UnitTypes.euros,
  summaryUnit: UnitTypes.euros,
  minPrefix: 1,
  views: {
    hour: 'pie',
    day: 'pie',
    week: 'pie',
    month: 'pie',
    year: 'pie',
  },
  traces: defaultTraces,
  maxHeight: maxHeight,
  hasSummary: true,
})
